import {
  ENTRY_DATA_LICENSEE_TABLE,
  ENTRY_DATA_LICENSEE_TABLE_KEYS,
  ENTRY_DATA_SECTION_KEYS,
  ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS,
  VENTURE_FORMATION_SUB_GROUP_SHELL_KEYS,
} from "@frontend/common";

import { DealsSection } from "@/types/deals.types";
import {
  useTableCreatorFocusStatus,
  DEFAULT_FOCUS_STATUS,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import {
  useValidatedInTableCreator,
  DEFAULT_VALIDATED_INFO,
} from "@FillInTableContainer/shared/context/ValidatedInTableCreatorContext";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleGroupShellReturn {
  deleteSubGroupShellData: () => void;
}

interface Props {
  positionPropsInfo: {
    groupTableKey: DealsSection;
    groupTableIndex: number;
    groupShellKey: string | null;
    groupShellIndex: number | null;
    subGroupShellIndex: number;
    subGroupShellKey: string | null;
    tableKey: string | null;
    tableIndex: number;
  };
}

export const useDeleteVentureFormationSubGroupShell = ({
  positionPropsInfo,
}: Props): UseHandleGroupShellReturn => {
  const {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    subGroupShellIndex,
    subGroupShellKey,
    tableKey,
    tableIndex,
  } = positionPropsInfo;

  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const { changeFocusStatus } = useTableCreatorFocusStatus();
  const { info, changeInfo } = useTableCreatorPositionInfo();
  const { changeValidatedInfo } = useValidatedInTableCreator();

  const deleteSubGroupShellData = () => {
    const deleteContingentSubGroupShell = (): ArticleEntryData => {
      if (info.groupShellIndex === null) return entryData;

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.deals]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.deals],
          [ENTRY_DATA_LICENSEE_TABLE]: entryData[
            ENTRY_DATA_SECTION_KEYS.deals
          ]?.[ENTRY_DATA_LICENSEE_TABLE]?.map((tableInfo, tableInfoIndex) => {
            if (tableInfoIndex === info.tableIndex) {
              const currentContingentInvestments =
                tableInfo[ENTRY_DATA_LICENSEE_TABLE_KEYS.ventureFormationTerms][
                  ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS
                    .contingentInvestmentAndDetailVf
                ];

              // 배열에서 해당 인덱스의 항목을 제거
              const updatedContingentInvestments =
                currentContingentInvestments.filter(
                  (_, index) => index !== info.subGroupShellIndex,
                );

              return {
                ...tableInfo,
                [ENTRY_DATA_LICENSEE_TABLE_KEYS.ventureFormationTerms]: {
                  ...tableInfo[
                    ENTRY_DATA_LICENSEE_TABLE_KEYS.ventureFormationTerms
                  ],
                  [ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS.contingentInvestmentAndDetailVf]:
                    updatedContingentInvestments,
                },
              };
            }
            return tableInfo;
          }),
        },
      };
    };

    if (
      info.subGroupShellKey ===
      VENTURE_FORMATION_SUB_GROUP_SHELL_KEYS.contingentInvestment
    ) {
      setEntryData(deleteContingentSubGroupShell());
      changeCurrentHistory({
        entryData: deleteContingentSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "delete",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
    }

    if (info.subGroupShellKey === "Asset Transfer Row") {
      const deleteAssetTransferRowSubGroupShell = (): ArticleEntryData => ({
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.deals]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.deals],
          [ENTRY_DATA_LICENSEE_TABLE]: entryData[
            ENTRY_DATA_SECTION_KEYS.deals
          ]?.[ENTRY_DATA_LICENSEE_TABLE]?.map((tableInfo, tableInfoIndex) => {
            if (tableInfoIndex !== info.tableIndex) return tableInfo;
            return {
              ...tableInfo,
              [ENTRY_DATA_LICENSEE_TABLE_KEYS.rightsScope]: tableInfo[
                ENTRY_DATA_LICENSEE_TABLE_KEYS.rightsScope
              ]?.filter((_, idx) => idx !== info.subGroupShellIndex),
            };
          }),
        },
      });

      setEntryData(deleteAssetTransferRowSubGroupShell());
      changeCurrentHistory({
        entryData: deleteAssetTransferRowSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "delete",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
    }

    if (info.subGroupShellKey === "Advisor") {
      const deleteAdvisorSubGroupShell = (): ArticleEntryData => ({
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.deals]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.deals],
          "Advisor Table": entryData[ENTRY_DATA_SECTION_KEYS.deals]?.[
            "Advisor Table"
          ]?.filter(
            (_, advisorInfoIndex) =>
              advisorInfoIndex !== info.subGroupShellIndex,
          ),
        },
      });

      setEntryData(deleteAdvisorSubGroupShell());
      changeCurrentHistory({
        entryData: deleteAdvisorSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "delete",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
    }

    if (info.subGroupShellKey === "Licensor(VF)") {
      const deleteLicensorVfSubGroupShell = (): ArticleEntryData => {
        const remainingLicensorVf =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (entryData[ENTRY_DATA_SECTION_KEYS.deals]?.["Licensor Table"] as any)?.[
            "Licensor(VF)"
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ]?.filter((_: any, idx: number) => idx !== info.subGroupShellIndex) ??
          [];
        const syncedLicensorName = remainingLicensorVf.flatMap(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (item: any) => item?.["Licensor Name(VF)"]?.value ?? [],
        );
        return {
          ...entryData,
          [ENTRY_DATA_SECTION_KEYS.deals]: {
            ...entryData[ENTRY_DATA_SECTION_KEYS.deals],
            "Licensor Table": {
              ...entryData[ENTRY_DATA_SECTION_KEYS.deals]?.["Licensor Table"],
              "Licensor(VF)": remainingLicensorVf,
              "Licensor Name": {
                ...(
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  entryData[ENTRY_DATA_SECTION_KEYS.deals]?.[
                    "Licensor Table"
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  ] as any
                )?.["Licensor Name"],
                value: syncedLicensorName,
              },
            },
          },
        };
      };

      setEntryData(deleteLicensorVfSubGroupShell());
      changeCurrentHistory({
        entryData: deleteLicensorVfSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "delete",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
    }

    if (info.subGroupShellKey === "Licensee(VF)") {
      const deleteLicenseeVfSubGroupShell = (): ArticleEntryData => ({
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.deals]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.deals],
          [ENTRY_DATA_LICENSEE_TABLE]: entryData[
            ENTRY_DATA_SECTION_KEYS.deals
          ]?.[ENTRY_DATA_LICENSEE_TABLE]?.map((tableInfo, tableInfoIndex) => {
            if (tableInfoIndex === info.tableIndex) {
              const remainingLicenseeVf =
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (tableInfo as any)["Licensee(VF)"]?.filter(
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (_: any, idx: number) => idx !== info.subGroupShellIndex,
                ) ?? [];
              const syncedLicenseeName = remainingLicenseeVf.flatMap(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (item: any) => item?.["Licensee Name(VF)"]?.value ?? [],
              );
              return {
                ...tableInfo,
                "Licensee(VF)": remainingLicenseeVf,
                [ENTRY_DATA_LICENSEE_TABLE_KEYS.licenseeName]: {
                  ...tableInfo[ENTRY_DATA_LICENSEE_TABLE_KEYS.licenseeName],
                  value: syncedLicenseeName,
                },
              };
            }
            return tableInfo;
          }),
        },
      });

      setEntryData(deleteLicenseeVfSubGroupShell());
      changeCurrentHistory({
        entryData: deleteLicenseeVfSubGroupShell(),
        changedDataInfo: {
          target: "subGroupShell",
          action: "delete",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex,
            subGroupShellKey,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
    }

    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
    changeFocusStatus(DEFAULT_FOCUS_STATUS);
    changeInfo(DEFAULT_POSITION_INFO);
  };

  return {
    deleteSubGroupShellData,
  };
};
